import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Building, User, Phone, Mail, Users, AlertTriangle, CheckCircle, Bed, DoorOpen } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const HostelReport = () => {
  const [hostels, setHostels] = useState([]);
  const [wardens, setWardens] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [hostelsRes, wardensRes, studentsRes] = await Promise.all([
        apiClient.get('/api/hostel/hostels'),
        apiClient.get('/api/hostel/wardens'),
        apiClient.get('/api/hostel/students?status=Active')
      ]);
      setHostels(Array.isArray(hostelsRes.data) ? hostelsRes.data : []);
      setWardens(Array.isArray(wardensRes.data) ? wardensRes.data : []);
      setStudents(Array.isArray(studentsRes.data) ? studentsRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get warden for a hostel
  const getWardenForHostel = (hostelId) => {
    return wardens.find(w => w.hostel_id === hostelId);
  };

  // Get student count for a hostel
  const getStudentCount = (hostelId) => {
    return students.filter(s => s.hostel_id === hostelId).length;
  };

  // Summary stats
  const totalHostels = hostels.length;
  const hostelsWithWarden = hostels.filter(h => getWardenForHostel(h.id)).length;
  const hostelsWithoutWarden = totalHostels - hostelsWithWarden;
  const totalCapacity = hostels.reduce((sum, h) => sum + (h.total_capacity || 0), 0);
  const totalStudents = students.length;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading report...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building className="w-6 h-6 text-blue-600" />
            Hostel Overview Report
          </h1>
          <p className="text-gray-500 text-sm mt-1">Quick view of all hostels with warden assignments</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-blue-600">Total Hostels</p>
                <p className="text-2xl font-bold text-blue-700">{totalHostels}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-green-600">With Warden</p>
                <p className="text-2xl font-bold text-green-700">{hostelsWithWarden}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${hostelsWithoutWarden > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-8 h-8 ${hostelsWithoutWarden > 0 ? 'text-red-600' : 'text-gray-400'}`} />
              <div>
                <p className={`text-sm ${hostelsWithoutWarden > 0 ? 'text-red-600' : 'text-gray-500'}`}>Without Warden</p>
                <p className={`text-2xl font-bold ${hostelsWithoutWarden > 0 ? 'text-red-700' : 'text-gray-600'}`}>{hostelsWithoutWarden}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-sm text-purple-600">Total Students</p>
                <p className="text-2xl font-bold text-purple-700">{totalStudents}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Bed className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-sm text-orange-600">Total Capacity</p>
                <p className="text-2xl font-bold text-orange-700">{totalCapacity}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hostel-Warden Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Hostel & Warden Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b">
                  <th className="text-left p-3 font-semibold">Hostel</th>
                  <th className="text-left p-3 font-semibold">Type</th>
                  <th className="text-center p-3 font-semibold">Rooms</th>
                  <th className="text-center p-3 font-semibold">Capacity</th>
                  <th className="text-center p-3 font-semibold">Students</th>
                  <th className="text-left p-3 font-semibold">Warden</th>
                  <th className="text-left p-3 font-semibold">Contact</th>
                  <th className="text-center p-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {hostels.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center p-8 text-gray-500">
                      <Building className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No hostels found.</p>
                    </td>
                  </tr>
                ) : (
                  hostels.map((hostel) => {
                    const warden = getWardenForHostel(hostel.id);
                    const studentCount = getStudentCount(hostel.id);
                    const occupancy = hostel.total_capacity > 0 
                      ? Math.round((studentCount / hostel.total_capacity) * 100) 
                      : 0;

                    return (
                      <tr key={hostel.id} className={`border-b hover:bg-gray-50 ${!warden ? 'bg-red-50' : ''}`}>
                        {/* Hostel Name */}
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              hostel.hostel_type === 'Boys' ? 'bg-blue-100' : 
                              hostel.hostel_type === 'Girls' ? 'bg-pink-100' : 'bg-purple-100'
                            }`}>
                              <Building className={`w-5 h-5 ${
                                hostel.hostel_type === 'Boys' ? 'text-blue-600' : 
                                hostel.hostel_type === 'Girls' ? 'text-pink-600' : 'text-purple-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium">{hostel.hostel_name}</p>
                              <p className="text-xs text-gray-500">{hostel.hostel_code}</p>
                            </div>
                          </div>
                        </td>

                        {/* Type */}
                        <td className="p-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            hostel.hostel_type === 'Boys' ? 'bg-blue-100 text-blue-700' : 
                            hostel.hostel_type === 'Girls' ? 'bg-pink-100 text-pink-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {hostel.hostel_type}
                          </span>
                        </td>

                        {/* Rooms */}
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <DoorOpen className="w-4 h-4 text-gray-400" />
                            <span>{hostel.total_rooms || 0}</span>
                          </div>
                        </td>

                        {/* Capacity */}
                        <td className="p-3 text-center">
                          <span className="font-medium">{hostel.total_capacity || 0}</span>
                        </td>

                        {/* Students */}
                        <td className="p-3 text-center">
                          <div>
                            <span className={`font-medium ${occupancy > 90 ? 'text-red-600' : occupancy > 70 ? 'text-orange-600' : 'text-green-600'}`}>
                              {studentCount}
                            </span>
                            <span className="text-xs text-gray-500 ml-1">({occupancy}%)</span>
                          </div>
                        </td>

                        {/* Warden */}
                        <td className="p-3">
                          {warden ? (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                <User className="w-4 h-4 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{warden.staff_name}</p>
                                <p className="text-xs text-gray-500">{warden.designation || 'Warden'}</p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-red-600">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="text-sm font-medium">Not Assigned</span>
                            </div>
                          )}
                        </td>

                        {/* Contact */}
                        <td className="p-3">
                          {warden ? (
                            <div className="space-y-1">
                              {warden.phone && (
                                <div className="flex items-center gap-1 text-xs">
                                  <Phone className="w-3 h-3 text-gray-400" />
                                  <span>{warden.phone}</span>
                                </div>
                              )}
                              {warden.email && (
                                <div className="flex items-center gap-1 text-xs">
                                  <Mail className="w-3 h-3 text-gray-400" />
                                  <span className="truncate max-w-[150px]">{warden.email}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="p-3 text-center">
                          {warden ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                              ✓ Active
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
                              ⚠ Needs Warden
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quick Legend */}
      <div className="flex items-center gap-6 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-100"></div>
          <span>Boys Hostel</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-pink-100"></div>
          <span>Girls Hostel</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-purple-100"></div>
          <span>Mixed Hostel</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-50 border border-red-200"></div>
          <span>Warden Not Assigned</span>
        </div>
      </div>
    </div>
  );
};

export default HostelReport;
